import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { BUSINESS } from "@/data/content";

const LINKS = [
  { label: "Chi siamo", href: "#chi-siamo" },
  { label: "Menu", href: "#menu" },
  { label: "Ordinazioni", href: "#ordinazioni" },
  { label: "Recensioni", href: "#recensioni" },
  { label: "Dove siamo", href: "#dove-siamo" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    const lenis = window.__lenis;
    if (open) {
      lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-[#E8E3D9]"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[116px] sm:h-[152px] flex items-center justify-between">
        <a href="#top" className="flex flex-col leading-none" data-testid="logo-link">
          <img
            src={BUSINESS.logo}
            alt="Laboratorio di Laurie"
            className={`h-16 sm:h-[80px] w-auto object-contain -ml-[1px] sm:-ml-[3px] transition-[filter] duration-500 ${scrolled ? "brightness-0" : ""}`}
          />
          <span className={`font-body text-[10px] sm:text-xs tracking-[0.28em] uppercase transition-colors duration-500 mt-2.5 ${scrolled ? "text-[#5C4F4A]" : "text-[#FDFBF7]/80"}`}>
            Panificio · Caffetteria
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9 self-start mt-6">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`link-underline font-body text-sm transition-colors duration-500 ${scrolled ? "text-[#2A1B14]/85 hover:text-[#2A1B14]" : "text-[#FDFBF7]/90 hover:text-[#FDFBF7]"}`}
              data-testid={`nav-${l.href.replace("#", "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3 self-start mt-5">
          <a
            href={BUSINESS.phoneHref}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#2A1B14] text-[#FDFBF7] px-5 py-2.5 text-sm font-medium hover:bg-[#3a271d] hover:scale-[0.98] transition-all duration-300"
            data-testid="header-call-btn"
          >
            <Phone size={15} />
            Chiama
          </a>
          <button
            onClick={() => setOpen(true)}
            className={`lg:hidden p-1 transition-colors duration-500 ${scrolled ? "text-[#2A1B14]" : "text-[#FDFBF7]"}`}
            aria-label="Apri menu"
            data-testid="mobile-menu-open"
          >
            <MenuIcon size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] h-[100dvh] bg-[#FDFBF7] lg:hidden flex flex-col overscroll-contain"
            data-testid="mobile-menu-panel"
          >
            <div className="flex items-center justify-between px-5 h-[84px] border-b border-[#E8E3D9] shrink-0">
              <img src={BUSINESS.logo} alt="Laboratorio di Laurie" className="h-14 w-auto object-contain brightness-0" />
              <button onClick={() => setOpen(false)} aria-label="Chiudi menu" data-testid="mobile-menu-close">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col p-6 gap-1">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-display text-3xl leading-tight py-3 border-b border-[#E8E3D9]/70 text-[#2A1B14]"
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href={BUSINESS.phoneHref}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1B14] text-[#FDFBF7] px-6 py-4 text-base font-medium shrink-0"
                data-testid="mobile-call-btn"
              >
                <Phone size={18} /> {BUSINESS.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
